import {
  AppBar,
  Badge,
  Box,
  ButtonGroup,
  Divider,
  Drawer,
  FormControl,
  Hidden,
  IconButton, InputAdornment, InputLabel, Link, MenuItem, NativeSelect, Popper, Select, Toolbar, Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ArrowBack, ArrowForward, Close, DuoRounded, Face, ForumOutlined, MenuOpenOutlined, Search } from '@material-ui/icons';
import { useAppConfigurations, useBaseStore, useHeaderHeight, useImpersonationOffset, useUserStore, ImpersonateHeader, useMeetCapabilities, useSearchBarStore } from '@roc/feature-app-core';
import { isElectronApp } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { NavigationCompany } from './navigationCompany';
import { MobileNavigationMenu, NavigationMenu } from './navigationMenu';
import { ProfileMenu } from './profileMenu';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { TechSupportChatContainer } from '../../../../feature-communication/src/communication/techSupportChatContainer';
import { SupportIconButton } from '@roc/feature-general-tasks';

import { SearchBar, SearchIcon } from './searchBar';
import { Button, ConditionalWrapper, Paper, TextField, Tooltip } from '@roc/ui';
import { useLogoutStore } from '@roc/feature-app-core';
import { useBackwardForwardNavigation } from '../../hooks/useBackwardForwardNavigation';
import clsx from 'clsx';

const drawerWidth = 250;
const NAV_INSET = 4;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    // Floating, fully rounded bar inset from the viewport edges. The bar is shortened by the
    // same inset, so the total header footprint (and the spacer below) stays at headerHeight.
    // while impersonating, the bar sits above the nav, so the nav moves down by that offset
    appBar: {
      top: (props: any) => NAV_INSET + (props.impersonationOffset ?? 0),
      left: NAV_INSET,
      right: NAV_INSET,
      width: 'auto',
      borderRadius: 16,
      boxShadow: theme.shadows[9],
      overflow: 'hidden',
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    // nav's own height = header height minus the impersonation offset (which only moves it down)
    imageHolder: {
      height: (props: any) => props.headerHeight - (props.impersonationOffset ?? 0) - NAV_INSET,
    },
    img: {
      display: 'block',
      padding: (props: any) => props.slimNavigation ? 6 : 10,
      maxHeight: (props: any) => props.headerHeight - (props.impersonationOffset ?? 0) - NAV_INSET - 2,
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerHeader: {
      height: (props: any) => props.headerHeight,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      justifyContent: 'flex-end',
    },
    spacer: {
      height: (props: any) => props.headerHeight,
    },
    videoRoomIconHolder: {
      '& .MuiBadge-badge': {
        top: 4
      },
      '& *': {
        color: 'white !important'
      },
    },
    videoRoomIconButton: {
      padding: 6,
      color: '#fff',
      // LoanChat Meet's own palette: ROC Blue 900 through to ROC Teal, rather than the older
      // indigo/cyan that matched nothing else in the product.
      background: 'linear-gradient(110deg, #041c2c 0%, #14506b 50%, #39bcd0 100%)',
      // The gradient is twice the button's width so the hover can actually slide it. Without
      // this, backgroundPosition has nothing to move through and the hover is a no-op.
      backgroundSize: '200% 100%',
      backgroundPosition: '0% 0%',
      boxShadow: '0 2px 8px rgba(57, 188, 208, 0.3)',
      transition: 'background-position 0.45s ease, box-shadow 0.25s ease',
      '& *': {
        color: 'white !important'
      },
      '&:hover': {
        backgroundPosition: '100% 0%',
        boxShadow: '0 3px 12px rgba(57, 188, 208, 0.45)',
      }
    },
    videoRoomButton: {
      width: 170,
      // 'capitalize' would re-case the wordmark; the label is already written as intended.
      textTransform: 'none',
      letterSpacing: 0.2,
      fontWeight: 700,
      borderRadius: 32,
      color: '#fff',
      background: 'linear-gradient(110deg, #041c2c 0%, #14506b 50%, #39bcd0 100%)',
      backgroundSize: '200% 100%',
      backgroundPosition: '0% 0%',
      boxShadow: '0 2px 10px rgba(57, 188, 208, 0.32)',
      transition: 'background-position 0.45s ease, box-shadow 0.25s ease, transform 0.15s ease',
      '& *': {
        color: 'white'
      },
      // Give the icon a little breathing room from the label at this width.
      '& .MuiButton-startIcon': {
        marginRight: 8,
      },
      '&:hover': {
        backgroundPosition: '100% 0%',
        boxShadow: '0 4px 16px rgba(57, 188, 208, 0.48)',
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      }
    },
    buttonGroup: {
      justifyContent: 'right',
      width: '100%',
      maxWidth: 200,
    },
    buttonDisabled: {
      '& *': {
        color: '#c5c5c5 !important'
      }
    },
    closeSearchButton: {
      padding: theme.spacing(0.5),
    },
    searchBar: {
      width: 300,
      marginLeft: 16,
      border: 0,
      '& input, & svg path': {
        color: theme.palette.text.primary
      },
      '& fieldset': {
        boxShadow: theme.shadows[0],
        borderColor: '#EFEFEF',
      },
    },
    // Loan search opens in a floating panel below the nav bar instead of inside it
    searchPopper: {
      zIndex: theme.zIndex.modal,
    },
    searchPanel: {
      width: 380,
      maxWidth: 'calc(100vw - 16px)',
      padding: theme.spacing(1),
      borderRadius: 999,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[9],
      '& .MuiAutocomplete-root': {
        width: '100%',
        marginLeft: 0,
      },
      '& .MuiAutocomplete-root .MuiPaper-root': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    crmPortalStyles: {
      '& .main-navigation-menu button .MuiButton-endIcon': {
        margin: 0,
      }
    }
  })
);

export interface NavigationProps {
  overrideAppLogo?: string;
  appBarClassName?: string;
  navigationMenuData?: any;
  profileMenuData?: any;
  redirectRoute?: any;
  loanRoutes?: any;
  possibleMatchesRoutes?: any;
  searchBar?: any;
  searchDrawer?: any;
  /** Search inputs rendered directly in the top row (crm-portal); the drawer is then only used on small screens */
  searchInline?: any;
  logoAsLink?: boolean;
  onLoanChatMeetClick?: () => void;
  showSupportIcon?: boolean;
  showCompanyDropdown?: boolean;
}

export const Navigation = observer(({
  overrideAppLogo,
  appBarClassName,
  navigationMenuData = [],
  profileMenuData = [],
  redirectRoute = '',
  loanRoutes = null,
  possibleMatchesRoutes = null,
  searchBar,
  searchDrawer,
  searchInline,
  logoAsLink = true,
  onLoanChatMeetClick,
  showSupportIcon = true,
  showCompanyDropdown = true
}: NavigationProps) => {
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const headerHeight = useHeaderHeight();
  const impersonationOffset = useImpersonationOffset();
  const classes = useStyles({
    searchBar,
    headerHeight,
    impersonationOffset,
    slimNavigation: globalStore.slimNavigation
  });
  const { appLogo } = useAppConfigurations();
  const [open, setOpen] = React.useState(false);
  const { logoutStore } = useLogoutStore();
  // Electron only: this toolbar is every portal's, and Meet is the desktop app's.
  const meetCapabilities = useMeetCapabilities(isElectronApp());
  const location = useLocation();
  const { canGoBack, canGoForward, goBack, goForward } = useBackwardForwardNavigation();
  const queryParams = new URLSearchParams(location.search);
  const logoLinkType = queryParams.get('logoLinkType') || 'ROUTER_LINK';
  const { searchBarStore } = useSearchBarStore();
  const searchAnchorRef = useRef<HTMLSpanElement>(null);
  const [isCrmPortal, setIsCrmPortal] = useState<boolean>(false);

  useEffect(() => {
    if (searchDrawer) {
      setIsCrmPortal(true);
    }
  }, [searchDrawer])

  const handleLogoClick = () => {
    // TODO
  };

  const toggleDrawer = event => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setOpen(false);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <div className={clsx(classes.root, isCrmPortal ? classes.crmPortalStyles : "")}>
      <ImpersonateHeader show={globalStore?.impersonatedUser} onExit={() => { logoutStore.logout() }} />
      <AppBar color="inherit" position="fixed" className={clsx(classes.appBar, appBarClassName)}>
        {
          isCrmPortal &&
          <Box position={'absolute'} width={'100%'} height={'auto'}>
            {/* top row: search (between the logo and the right edge) + company switcher */}
            <Box display="flex" alignItems="center" pt="8px" pl={{ xs: 2, md: 36 }} pr={3}>
              <Box flex={1} minWidth={0} position={'relative'} zIndex={1}>
                {searchInline &&
                  <Hidden smDown implementation="css">
                    <Box display={'flex'} width={'100%'} maxWidth={980} margin={'0 auto'}>
                      {searchInline}
                    </Box>
                  </Hidden>
                }
                <Box display={searchInline ? { xs: 'flex', md: 'none' } : 'flex'}>
                  <Button
                    testId='SearchButton'
                    variant='outlined'
                    startIcon={<Search />}
                    title='Search CRM (or) Loans'
                    onClick={() => searchBarStore.setShowSearch(true)}
                    style={{
                      height: 26,
                      margin: '4px auto',
                      width: '100%',
                      maxWidth: 600,
                      justifyContent: 'flex-start',
                      textTransform: 'unset',
                      fontSize: 12,
                      fontWeight: 'normal',
                    }}
                  >
                    Search CRM (or) Loans ...
                  </Button>
                </Box>
              </Box>
              {/* company switcher sits in the top row so the menu row has more room */}
              {showCompanyDropdown && globalStore.userFeatures?.allowLoanSearch && globalStore.userFeatures?.showMultiCompanyDropdown && !isElectronApp() &&
                <Box ml={2} flexShrink={0} position={'relative'} zIndex={1}>
                  <NavigationCompany redirectRoute={redirectRoute} compact />
                </Box>
              }
            </Box>
          </Box>
        }
        <Toolbar style={{
          minHeight: 'auto',
          overflow: 'auto'
        }}>
          <Hidden mdUp implementation="css">
            {
              navigationMenuData.length ? <IconButton
                edge="start"
                className={classes.menuButton}
                style={{ transform: 'rotate(180deg)' }}
                color="primary"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
              >
                <MenuOpenOutlined />
              </IconButton> : null
            }
          </Hidden>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            className={classes.imageHolder}
          >
            {(overrideAppLogo || appLogo) && (
              <ConditionalWrapper
                condition={logoAsLink}
                wrapper={(children) => {
                  return (
                    logoLinkType === 'HTML_LINK' ?
                      <a href={'/'}>
                        {children}
                      </a>
                      :
                      <RouterLink to={globalStore.routeBaseUrl}>
                        {children}
                      </RouterLink>
                  )
                }}>
                <img
                  className={classes.img}
                  alt="App Logo"
                  src={overrideAppLogo ? overrideAppLogo : appLogo}
                  onClick={() => handleLogoClick()}
                />
              </ConditionalWrapper>
            )}
          </Box>
          <EnvironmentText />
          <Box display="flex" flexDirection="row" maxWidth="100%" overflow="auto" flexGrow={1} style={{
            paddingTop: isCrmPortal ? 42 : 0
          }} className='main-navigation-menu'>
            <Box flexGrow="1">
              <Hidden smDown implementation="css">
                <NavigationMenu data={navigationMenuData} isCrmPortal={isCrmPortal} />
              </Hidden>
            </Box>
          </Box>
          {false && userStore.allowLoanPricing && !isElectronApp() &&
            <Box display="flex" alignItems="center" justifyContent="flex-end" minWidth={80} style={{
              paddingTop: isCrmPortal ? 34 : 0
            }}>
              <TechSupportChatContainer />
            </Box>
          }
          {showSupportIcon && userStore.allowLoanPricing && !isElectronApp() &&
            <Box display="flex" alignItems="center" justifyContent="flex-end" minWidth={80} style={{
              paddingTop: isCrmPortal ? 34 : 0
            }}>
              <SupportIconButton />
            </Box>
          }
          {
            globalStore.isLoanChatApp &&
            <ButtonGroup variant='outlined' size="large" color="primary" className={classes.buttonGroup}>
              <Tooltip title="Back in history" placement='bottom' arrow>
                <IconButton
                  onClick={() => {
                    goBack();
                  }}
                  className={!canGoBack ? classes.buttonDisabled : ''}
                  disabled={!canGoBack}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Tooltip title="Forward in history" placement='bottom' arrow>
                <IconButton
                  onClick={() => {
                    goForward();
                  }}
                  className={!canGoForward ? classes.buttonDisabled : ''}
                  disabled={!canGoForward}>
                  <ArrowForward />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          }
          {
            searchBar ?
              <Box display="flex" flexDirection="row" maxWidth="100%" width={"100%"} justifyContent="center">
                {
                  searchBar
                }
              </Box> : null
          }
          {
            searchDrawer ? (
              <>
                <Drawer
                  anchor={'top'}
                  open={searchBarStore.showSearch}
                  onClose={() => searchBarStore.setShowSearch(false)}
                >
                  {searchDrawer}
                </Drawer>
                <Box display="flex" flexDirection="row" maxWidth="100%" alignItems="center" justifyContent="flex-end" marginRight="15px" style={{
                  paddingTop: isCrmPortal ? 42 : 0
                }}>
                  {/* <SearchIcon onClick={() => searchBarStore.setShowSearch(true)} /> */}

                </Box>
              </>
            ) : null
          }
          {
            showCompanyDropdown && globalStore.userFeatures?.allowLoanSearch && !isElectronApp() ?
              <Box display="flex" flexDirection="row" maxWidth="100%" alignItems="center" justifyContent="flex-end" style={{
                paddingTop: isCrmPortal ? 34 : 0
              }}>
                <>
                  {!searchDrawer &&
                    <>
                      <span ref={searchAnchorRef}>
                        <SearchIcon onClick={() => searchBarStore.setShowSearch(true)} />
                      </span>
                      <Popper
                        open={searchBarStore.showSearch && !!searchAnchorRef.current}
                        anchorEl={searchAnchorRef.current}
                        placement="bottom-end"
                        className={classes.searchPopper}
                        modifiers={{ offset: { offset: '0,28' } }}
                      >
                        <Box className={classes.searchPanel}>
                          <SearchBar loanRoutes={loanRoutes} onClose={() => searchBarStore.setShowSearch(false)} />
                        </Box>
                      </Popper>
                    </>
                  }
                  {globalStore.userFeatures?.showMultiCompanyDropdown && !isCrmPortal ?
                    <NavigationCompany redirectRoute={redirectRoute} /> : null
                  }
                </>
              </Box> : null
          }

          {
            /*
             * Whether Meet exists here is the server's answer now, not a constant in
             * environment.ts — see MeetCapabilitiesStore. It starts false and turns on when the
             * response lands, so the button appears a moment after the rest of the toolbar
             * rather than flashing for someone who is not entitled to it.
             */
            isElectronApp() && meetCapabilities?.meetAvailable ?
              <Box display="flex" alignItems="center" justifyContent="flex-end" mx={1} className={classes.videoRoomIconHolder}>
                <ConditionalWrapper
                  condition={new Date("2026-12-31") >= new Date()}
                  wrapper={children => {
                    return (
                      <Badge
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        badgeContent={'New'}
                        color={'error'}
                      >
                        {children}
                      </Badge>
                    )
                  }}>
                  <Hidden mdUp implementation="css">
                    <IconButton
                      className={classes.videoRoomIconButton}
                      onClick={onLoanChatMeetClick}>
                      <DuoRounded />
                    </IconButton>
                  </Hidden>
                  <Hidden smDown implementation="css">
                    <Button testId='loanChatMeet'
                      variant='contained'
                      startIcon={<DuoRounded />}
                      className={classes.videoRoomButton}
                      onClick={onLoanChatMeetClick}>
                      LoanChat Meet
                    </Button>
                  </Hidden>
                </ConditionalWrapper>
              </Box> : null
          }
          {
            profileMenuData.length ?
              <Box display="flex" alignItems="center" justifyContent="flex-end" ml={1} style={{
                paddingTop: isCrmPortal ? 34 : 0
              }}>
                <IconButton
                  aria-label="show unread messages"
                  color="inherit"
                  style={{ display: 'none' }}
                >
                  <Badge badgeContent={10} color="error">
                    <ForumOutlined />
                  </Badge>
                </IconButton>
                <ProfileMenu data={profileMenuData} />
              </Box> : null
          }
        </Toolbar>
      </AppBar>
      <Hidden mdUp implementation="css">
        <Drawer
          className={classes.drawer}
          anchor="left"
          open={open}
          onClose={toggleDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div className={classes.drawerHeader}>
            <IconButton onClick={handleDrawerClose}>
              <MenuOpenOutlined />
            </IconButton>
          </div>
          <Divider />
          <MobileNavigationMenu
            data={navigationMenuData}
            onMenuItemClick={() => setOpen(false)}
          />
        </Drawer>
      </Hidden>
      <div className={classes.spacer} />
    </div>
  );
});


const useEnvironmentTextStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Environment badge shown inside the nav bar, next to the logo
    environmentText: {
      flexShrink: 0,
      alignSelf: 'center',
      margin: theme.spacing(0, 1.5, 0, 0.5),
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 11,
      lineHeight: '18px',
      fontWeight: 700,
      letterSpacing: 1,
      // '&&' beats the AppBar theme override that recolors every descendant
      '&&': {
        color: '#fff',
      },
    }
  })
);

export const EnvironmentText = observer(() => {
  const classes = useEnvironmentTextStyles();
  const { userStore } = useUserStore();
  useEffect(() => {
    userStore.getDeployEnvironment();
  }, []);
  const getColor = () => {
    switch (userStore.deployEnvironment) {
      case 'QA':
        return '#2196f3';
      case 'STG':
        return 'green';
      default:
        return '#535353';
    }
  }

  const show = userStore.deployEnvironment == 'LOCAL'
    || userStore.deployEnvironment == 'DEV'
    || userStore.deployEnvironment == 'QA'
    || userStore.deployEnvironment == 'STG';

  return (
    show ? <Box className={classes.environmentText} bgcolor={getColor()}>
      {userStore.deployEnvironment}
    </Box> : null
  )
});

export default Navigation;
